import { useEffect, useId, useRef, useState } from 'react'
import {
  faBell,
  faCheckDouble,
  faVolumeHigh,
  faVolumeXmark,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { useQueryClient } from '@tanstack/react-query'

import type { NotificationView } from '@contracts/notifications.types'

import { useAdminExtensions } from '../../extensions/hooks'
import { Icon, IconButton } from '../../shared/components'
import { useUiStore } from '../../shared/hooks'
import { useT } from '../../shared/i18n'

import {
  useNotificationList,
  useNotificationSound,
  useNotificationSummary,
} from './hooks'
import type { NotificationBellProps } from './NotificationBell.types'
import { notificationsApi } from './notifications.api'

function relativeTime(iso: string, locale: string): string {
  const seconds = Math.round((new Date(iso).getTime() - Date.now()) / 1000)
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
  const abs = Math.abs(seconds)

  if (abs < 60) return rtf.format(seconds, 'second')
  if (abs < 3600) return rtf.format(Math.round(seconds / 60), 'minute')
  if (abs < 86_400) return rtf.format(Math.round(seconds / 3600), 'hour')
  if (abs < 7 * 86_400) return rtf.format(Math.round(seconds / 86_400), 'day')

  return new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : locale, {
    dateStyle: 'medium',
  }).format(new Date(iso))
}

/**
 * Notification center in the top bar (ADR-047): a bell with the number of
 * unseen notifications, a dropdown with the latest ones, and a short sound
 * when a new one arrives.
 */
export function NotificationBell({ onOpenLink }: NotificationBellProps) {
  const { t, locale } = useT()
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const panelId = useId()
  const summary = useNotificationSummary()
  const list = useNotificationList(open, locale, summary.data?.latestAt)
  const extensions = useAdminExtensions()
  const sound = useUiStore((s) => s.notificationSound)
  const setSound = useUiStore((s) => s.setNotificationSound)

  useNotificationSound(summary.data?.latestAt)

  const unseen = summary.data?.unseen ?? 0
  const refresh = () => qc.invalidateQueries({ queryKey: ['notifications'] })

  // Opening the dropdown marks everything shown as seen (the badge clears).
  useEffect(() => {
    if (open && unseen > 0)
      void notificationsApi.seen().then(refresh, () => undefined)
  }, [open, unseen])

  useEffect(() => {
    if (!open) return

    const onDown = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false)
    }

    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)

    document.addEventListener('pointerdown', onDown)
    document.addEventListener('keydown', onKey)

    return () => {
      document.removeEventListener('pointerdown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  if (summary.isError && !summary.data) return null

  const sourceLabel = (source: string) => {
    if (source.startsWith('plugin:'))
      return (
        extensions.plugins.find((p) => p.key === source.slice(7))?.name ??
        source.slice(7)
      )

    return (t.notifications.sources as Record<string, string>)[source] ?? source
  }

  const openItem = async (n: NotificationView) => {
    if (!n.readAt) await notificationsApi.read(n.id).catch(() => undefined)
    void refresh()

    if (n.link) {
      setOpen(false)
      onOpenLink(n.link)
    }
  }

  const badge = unseen > 99 ? '99+' : String(unseen)

  return (
    <div className="notif" ref={root}>
      <button
        type="button"
        className="icon-btn notif__button"
        aria-label={
          unseen
            ? `${t.notifications.title} (${t.notifications.unseen.replace('{count}', badge)})`
            : t.notifications.title
        }
        title={t.notifications.title}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen(!open)}
      >
        <Icon icon={faBell} />
        {unseen > 0 && (
          <span className="notif__badge" aria-hidden>
            {badge}
          </span>
        )}
      </button>
      {open && (
        <div
          className="notif__panel"
          id={panelId}
          role="region"
          aria-label={t.notifications.title}
        >
          <div className="notif__head">
            <strong>{t.notifications.title}</strong>
            <span className="notif__actions">
              <IconButton
                icon={sound ? faVolumeHigh : faVolumeXmark}
                label={
                  sound ? t.notifications.soundOff : t.notifications.soundOn
                }
                onClick={() => setSound(!sound)}
              />
              <IconButton
                icon={faCheckDouble}
                label={t.notifications.markAllRead}
                disabled={!summary.data?.unread}
                onClick={() =>
                  void notificationsApi.readAll().then(refresh, () => undefined)
                }
              />
            </span>
          </div>
          {list.isLoading && <p className="notif__empty">{t.common.loading}</p>}
          {list.data && !list.data.length && (
            <p className="notif__empty">{t.notifications.empty}</p>
          )}
          {!!list.data?.length && (
            <ul className="notif__list">
              {list.data.map((n) => (
                <li
                  key={n.id}
                  className={`notif__item notif__item--${n.severity.toLowerCase()}${n.readAt ? '' : ' notif__item--unread'}`}
                >
                  <button
                    type="button"
                    className="notif__open"
                    onClick={() => void openItem(n)}
                  >
                    <span className="notif__title">{n.title}</span>
                    {n.body && <span className="notif__body">{n.body}</span>}
                    <span className="notif__meta">
                      {sourceLabel(n.source)} ·{' '}
                      <time dateTime={n.createdAt}>
                        {relativeTime(n.createdAt, locale)}
                      </time>
                    </span>
                  </button>
                  <IconButton
                    icon={faXmark}
                    label={t.notifications.dismiss}
                    onClick={() =>
                      void notificationsApi
                        .dismiss(n.id)
                        .then(refresh, () => undefined)
                    }
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
